/**
 * Servicio API para consumir endpoints de alertas
 * Ubicación: src/services/alertsApi.js
 */

import axios from 'axios';

// Usar la misma API productiva que el resto de la aplicación
// REACT_APP_API_URL = https://api.cloud.winguservices.com (definida en .env)
const getApiBaseUrl = () => {
  const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
  return `${baseUrl}/ops/alerts`;
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para incluir token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const alertsApi = {
  /**
   * Obtener alertas activas con filtros opcionales
   * @param {Object} params - { provider, region, severity, service_name, limit, offset }
   * @returns {Promise}
   */
  getActiveAlerts: (params = {}) => {
    return api.get('/active', { params });
  },

  /**
   * Obtener resumen de alertas (KPIs)
   * @returns {Promise}
   */
  getAlertsSummary: () => {
    return api.get('/summary/overview');
  },

  /**
   * Obtener conteo de alertas por proveedor
   * @returns {Promise}
   */
  getAlertsByProvider: () => {
    return api.get('/summary/by-provider');
  },

  /**
   * Obtener conteo de alertas por región
   * Útil para visualizar en mapa
   * @returns {Promise}
   */
  getAlertsByRegion: () => {
    return api.get('/summary/by-region');
  },

  /**
   * Obtener alertas multi-nube agregadas
   * @param {Object} params - { nube, days, limit, cliente, fecha_inicio, fecha_fin }
   * @returns {Promise}
   */
  getMultiCloudAlerts: (params = {}) => {
    return api.get('/multi-cloud', { params });
  },

  /**
   * Obtener estado actual de alertas
   * @param {Object} params - { is_active, severity_min, limit }
   * @returns {Promise}
   */
  getCurrentAlertsState: (params = {}) => {
    return api.get('/current-state', { params });
  },

  /**
   * Obtener detalles completos de una alerta
   * @param {string} alertId - ID de la alerta
   * @returns {Promise}
   */
  getAlertDetails: (alertId) => {
    return api.get(`/${alertId}`);
  },

  /**
   * Obtener línea de tiempo de cambios de una alerta
   * @param {string} alertId - ID de la alerta
   * @param {number} days - Días históricos (default 30)
   * @returns {Promise}
   */
  getAlertTimeline: (alertId, days = 30) => {
    return api.get(`/${alertId}/timeline`, { params: { days } });
  },

  /**
   * Health check del servicio
   * @returns {Promise}
   */
  healthCheck: () => {
    return api.get('/health');
  }
};

export default alertsApi;
